import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowPathIcon,
  LockClosedIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

import { usePDFReader } from "./usePDFReader";

// Configure PDF.js worker: prioritize local public directory, fallback to versioned cdnjs
if (typeof window !== "undefined") {
  const publicUrl = process.env.PUBLIC_URL || "";
  pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.js`;
}

const documentOptions = {
  cMapUrl: `${process.env.PUBLIC_URL || ""}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `${process.env.PUBLIC_URL || ""}/standard_fonts/`,
};

export default function PDFReader() {
  const {
    itemDetail,
    userData,
    pdfSource,
    numPages,
    pageNumber,
    scale,
    rotation,
    viewMode,
    themeMode,
    isSidebarOpen,
    isFullscreen,
    isLoading,
    downloadProgress,
    loadError,
    hasNoFileUploaded,
    useSampleFallback,
    containerRef,
    prevPage,
    nextPage,
    goToPage,
    zoomIn,
    zoomOut,
    resetZoom,
    rotateClockwise,
    toggleFullscreen,
    setViewMode,
    setThemeMode,
    setIsSidebarOpen,
    goBack,
    handleContextMenu,
    handleDragStart,
    handleEnableSamplePreview,
    handleRetry,
    onDocumentLoadSuccess,
    onDocumentLoadError,
  } = usePDFReader();

  const [inputPage, setInputPage] = useState(pageNumber);

  useEffect(() => {
    setInputPage(pageNumber);
  }, [pageNumber]);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    goToPage(inputPage);
  };

  // Theme styling definitions
  const themeClasses = {
    dark: {
      bg: "bg-[#09090b]",
      header: "bg-[#18181b]/95 border-zinc-800 text-white shadow-xl shadow-black/40",
      contentBg: "bg-[#09090b]",
      dock: "bg-[#18181b]/95 border-zinc-700/80 text-white shadow-2xl shadow-black/60",
      sidebar: "bg-[#121215] border-zinc-800 text-white",
      textMuted: "text-zinc-400",
      btnHover: "hover:bg-zinc-800 active:bg-zinc-700",
      dockBtn: "hover:bg-zinc-800 text-zinc-300 hover:text-white",
      pageCard: "shadow-2xl shadow-black/70 ring-1 ring-zinc-800/80",
    },
    sepia: {
      bg: "bg-[#fbf0d9]",
      header: "bg-[#f4e4c1]/95 border-[#e2d0a8] text-amber-950 shadow-md",
      contentBg: "bg-[#fbf0d9]",
      dock: "bg-[#f4e4c1]/95 border-[#e2d0a8] text-amber-950 shadow-xl shadow-amber-950/10",
      sidebar: "bg-[#f6ebd1] border-[#e2d0a8] text-amber-950",
      textMuted: "text-amber-800/80",
      btnHover: "hover:bg-[#eddaba] active:bg-[#e4cdab]",
      dockBtn: "hover:bg-[#eddaba] text-amber-900",
      pageCard: "shadow-xl shadow-amber-950/15 ring-1 ring-amber-900/10",
    },
    light: {
      bg: "bg-zinc-100",
      header: "bg-white/95 border-zinc-200 text-zinc-900 shadow-md",
      contentBg: "bg-zinc-100",
      dock: "bg-white/95 border-zinc-200 text-zinc-900 shadow-xl shadow-zinc-400/20",
      sidebar: "bg-zinc-50 border-zinc-200 text-zinc-900",
      textMuted: "text-zinc-500",
      btnHover: "hover:bg-zinc-200 active:bg-zinc-300",
      dockBtn: "hover:bg-zinc-100 text-zinc-700 hover:text-zinc-950",
      pageCard: "shadow-xl shadow-zinc-400/30 ring-1 ring-zinc-200",
    },
  }[themeMode];

  const bookTitle = itemDetail?.title || "E-Book Reader";
  const authorName = itemDetail?.author || itemDetail?.vendor_details?.name || "BAB Publishing";
  const watermarkText = `BAB DIGITAL • ${userData?.email || userData?.name || "OFFICIAL EDITION"
    } • DO NOT DISTRIBUTE`;

  return (
    <div
      ref={containerRef}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      className={`relative w-screen h-screen overflow-hidden flex flex-col select-none transition-colors duration-200 ${themeClasses.bg}`}
    >
      {/* =========================================================================
          TOP HEADER CONTROLS
          ========================================================================= */}
      <header
        className={`z-30 h-14 px-3 sm:px-6 flex items-center justify-between border-b backdrop-blur-md transition-colors ${themeClasses.header}`}
      >
        {/* Left: Back & Title info */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 max-w-[50%]">
          <button
            onClick={goBack}
            className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 ${themeClasses.btnHover}`}
            title="Return to previous page"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider">
              Back
            </span>
          </button>

          <div className="h-6 w-px bg-zinc-700/40 hidden sm:block" />

          <div className="min-w-0 truncate">
            <h1 className="text-sm sm:text-base font-bold truncate leading-tight">
              {bookTitle}
            </h1>
            <p className={`text-[11px] truncate leading-tight ${themeClasses.textMuted}`}>
              {authorName}
            </p>
          </div>
        </div>

        {/* Center: Security / Protection Pill */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/30">
          {isLoading ? (
            <div className="w-3.5 h-3.5 border-2 border-secondary/30 border-t-secondary rounded-full animate-spin" />
          ) : (
            <LockClosedIcon className="w-3.5 h-3.5 text-secondary animate-pulse" />
          )}
          <span className="text-[11px] font-bold text-secondary tracking-wide uppercase">
            {isLoading
              ? downloadProgress > 0 && downloadProgress < 100
                ? `Downloading (${downloadProgress}%)...`
                : "Securing & Loading..."
              : "Protected Digital Reader • Offline Save & Print Disabled"}
          </span>
        </div>

        {/* Right: View mode, Theme, Sidebar & Fullscreen toggles */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Continuous / Single Page layout toggle */}
          <div className="hidden md:flex items-center bg-black/10 rounded-lg p-0.5 border border-zinc-700/30">
            <button
              onClick={() => setViewMode("single")}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${viewMode === "single"
                  ? "bg-secondary text-black font-bold shadow"
                  : themeClasses.textMuted
                }`}
              title="Single Page Mode"
            >
              Single
            </button>
            <button
              onClick={() => setViewMode("continuous")}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${viewMode === "continuous"
                  ? "bg-secondary text-black font-bold shadow"
                  : themeClasses.textMuted
                }`}
              title="Continuous Scroll Mode"
            >
              Continuous
            </button>
          </div>

          {/* Theme switcher */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setThemeMode("dark")}
              className={`p-1.5 rounded-md transition-colors ${themeMode === "dark"
                  ? "text-secondary ring-1 ring-secondary/50 bg-secondary/10"
                  : themeClasses.btnHover
                }`}
              title="Dark Mode"
            >
              <MoonIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setThemeMode("sepia")}
              className={`p-1.5 rounded-md transition-colors ${themeMode === "sepia"
                  ? "text-amber-800 ring-1 ring-amber-700/50 bg-amber-200"
                  : themeClasses.btnHover
                }`}
              title="Warm Sepia Mode"
            >
              <span className="w-4 h-4 rounded-full bg-[#e8cf99] border border-amber-800/40 inline-block" />
            </button>
            <button
              onClick={() => setThemeMode("light")}
              className={`p-1.5 rounded-md transition-colors ${themeMode === "light"
                  ? "text-amber-600 ring-1 ring-amber-400 bg-amber-100"
                  : themeClasses.btnHover
                }`}
              title="Light Mode"
            >
              <SunIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="h-6 w-px bg-zinc-700/40 hidden sm:block mx-1" />

          {/* Sidebar Toggle */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`p-2 rounded-lg transition-colors ${isSidebarOpen ? "text-secondary bg-secondary/10" : themeClasses.btnHover
              }`}
            title="Page Thumbnails"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className={`p-2 rounded-lg transition-colors ${themeClasses.btnHover}`}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="w-5 h-5" />
            ) : (
              <ArrowsPointingOutIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </header>

      {/* =========================================================================
          MAIN VIEWER BODY
          ========================================================================= */}
      <div className="relative flex-1 flex overflow-hidden">
        {/* Document Canvas Area */}
        <main
          className={`flex-1 overflow-auto pdf-reader-scrollbar flex flex-col items-center justify-start py-8 px-4 transition-colors ${themeClasses.contentBg}`}
        >
          {/* Missing File or Empty State Handler */}
          {hasNoFileUploaded && !pdfSource ? (
            <div className="max-w-md w-full my-auto p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center shadow-2xl">
              <div className="w-16 h-16 rounded-2xl bg-secondary/10 border border-secondary/30 flex items-center justify-center mx-auto mb-4">
                <BookOpenIcon className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                Digital Edition In Preparation
              </h2>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                The reading file for <span className="text-white font-medium">"{bookTitle}"</span> is
                currently being prepared by the publisher. You can view our demo sample book below to
                experience the reader.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleEnableSamplePreview}
                  className="flex-1 py-2.5 px-4 bg-secondary text-black font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-secondary/90 transition-all shadow-lg shadow-secondary/20"
                >
                  Read Sample Preview
                </button>
                <button
                  onClick={goBack}
                  className="flex-1 py-2.5 px-4 bg-zinc-800 text-white font-medium text-xs uppercase tracking-wider rounded-xl hover:bg-zinc-700 transition-all border border-zinc-700"
                >
                  Return to Library
                </button>
              </div>
            </div>
          ) : loadError ? (
            <div className="max-w-md w-full my-auto p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-red-900/40 text-center shadow-2xl">
              <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-3">
                <LockClosedIcon className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">Unable to Load Digital File</h3>
              <p className="text-xs text-zinc-400 mb-6 leading-relaxed">{loadError}</p>
              <div className="flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={handleRetry}
                  className="flex-1 py-2.5 px-3 bg-secondary text-black font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-secondary/90 transition-all shadow-lg shadow-secondary/20"
                >
                  Retry Loading
                </button>
                <button
                  onClick={handleEnableSamplePreview}
                  className="flex-1 py-2.5 px-3 bg-zinc-800 text-white font-medium text-xs uppercase tracking-wider rounded-xl hover:bg-zinc-700 transition-all border border-zinc-700"
                >
                  Load Sample Book
                </button>
                <button
                  onClick={goBack}
                  className="py-2.5 px-3 bg-black/40 text-zinc-400 hover:text-white font-medium text-xs uppercase rounded-xl transition-all"
                >
                  Back
                </button>
              </div>
            </div>
          ) : !pdfSource ? (
            <div className="flex flex-col items-center justify-center my-auto p-16 gap-4">
              <div className="w-14 h-14 rounded-full border-4 border-secondary/20 border-t-secondary animate-spin" />
              <p className="text-sm uppercase tracking-widest font-bold text-secondary animate-pulse">
                {downloadProgress > 0 && downloadProgress < 100
                  ? `Downloading E-Book (${downloadProgress}%)...`
                  : "Securing & Preparing Digital Edition..."}
              </p>
            </div>
          ) : (
            <div className="relative flex flex-col items-center">
              {/* Fallback Sample Banner notification if active */}
              {useSampleFallback && (
                <div className="mb-4 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                  Showing sample preview edition for reader testing.
                </div>
              )}

              {/* Single Page Navigation Chevron Buttons (Left/Right) */}
              {viewMode === "single" && numPages > 1 && (
                <>
                  <button
                    onClick={prevPage}
                    disabled={pageNumber <= 1}
                    className={`fixed left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full backdrop-blur-md border transition-all ${pageNumber <= 1
                        ? "opacity-20 cursor-not-allowed bg-black/10 border-transparent"
                        : "bg-zinc-900/80 hover:bg-secondary hover:text-black border-zinc-700/60 text-white shadow-xl hover:scale-110 active:scale-95"
                      }`}
                    title="Previous Page (Left Arrow)"
                  >
                    <ChevronLeftIcon className="w-6 h-6" />
                  </button>

                  <button
                    onClick={nextPage}
                    disabled={pageNumber >= numPages}
                    className={`fixed right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full backdrop-blur-md border transition-all ${pageNumber >= numPages
                        ? "opacity-20 cursor-not-allowed bg-black/10 border-transparent"
                        : "bg-zinc-900/80 hover:bg-secondary hover:text-black border-zinc-700/60 text-white shadow-xl hover:scale-110 active:scale-95"
                      }`}
                    title="Next Page (Right Arrow)"
                  >
                    <ChevronRightIcon className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* The Protected PDF Document */}
              <Document
                file={pdfSource}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={documentOptions}
                loading={
                  <div className="flex flex-col items-center justify-center p-16 gap-4">
                    <div className="w-12 h-12 rounded-full border-4 border-secondary/20 border-t-secondary animate-spin" />
                    <p className="text-xs uppercase tracking-widest font-bold text-secondary animate-pulse">
                      Loading Protected E-Book...
                    </p>
                  </div>
                }
                className={
                  viewMode === "continuous"
                    ? "flex flex-col items-center gap-8"
                    : "flex flex-col items-center"
                }
              >
                {viewMode === "single" ? (
                  <div
                    className={`relative rounded-md overflow-hidden pdf-secure-canvas transition-transform ${themeClasses.pageCard}`}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      rotate={rotation}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={
                        <div className="w-[300px] sm:w-[500px] h-[700px] bg-zinc-900/40 animate-pulse rounded-md flex items-center justify-center text-xs text-zinc-500">
                          Rendering Page {pageNumber}...
                        </div>
                      }
                    />

                    {/* Subtle Copyright Anti-Theft Watermark Overlay */}
                    <div className="absolute inset-x-0 bottom-2 pointer-events-none flex justify-center">
                      <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500/25 select-none">
                        {watermarkText}
                      </span>
                    </div>
                  </div>
                ) : (
                  Array.from(new Array(numPages || 0), (_, index) => (
                    <div
                      key={`page_${index + 1}`}
                      id={`pdf-page-${index + 1}`}
                      className={`relative rounded-md overflow-hidden pdf-secure-canvas transition-transform ${themeClasses.pageCard}`}
                    >
                      <Page
                        pageNumber={index + 1}
                        scale={scale}
                        rotate={rotation}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <div className="w-[300px] sm:w-[500px] h-[700px] bg-zinc-900/40 animate-pulse rounded-md flex items-center justify-center text-xs text-zinc-500">
                            Rendering Page {index + 1}...
                          </div>
                        }
                      />
                      <div className="absolute inset-x-0 bottom-2 pointer-events-none flex justify-center">
                        <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-500/25 select-none">
                          {watermarkText}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </Document>
            </div>
          )}

          {/* Bottom spacing to clear dock */}
          <div className="h-24 w-full flex-shrink-0" />
        </main>

        {/* =========================================================================
            SIDEBAR: THUMBNAILS DRAWER
            ========================================================================= */}
        {isSidebarOpen && (
          <aside
            className={`w-72 sm:w-80 h-full border-l flex flex-col z-20 shadow-2xl transition-all ${themeClasses.sidebar}`}
          >
            <div className="h-14 px-4 flex items-center justify-between border-b border-zinc-700/30">
              <div className="flex items-center gap-2">
                <DocumentDuplicateIcon className="w-5 h-5 text-secondary" />
                <h3 className="text-xs font-bold uppercase tracking-wider">Page Thumbnails</h3>
              </div>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className={`p-1.5 rounded-lg ${themeClasses.btnHover}`}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pdf-reader-scrollbar p-4 space-y-4">
              {numPages ? (
                Array.from(new Array(numPages), (_, idx) => {
                  const pNum = idx + 1;
                  const isActive = pNum === pageNumber;
                  return (
                    <div
                      key={`thumb_${pNum}`}
                      onClick={() => {
                        goToPage(pNum);
                        if (viewMode === "continuous") {
                          const el = document.getElementById(`pdf-page-${pNum}`);
                          el?.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className={`cursor-pointer rounded-xl p-2.5 transition-all flex flex-col items-center gap-2 border ${isActive
                          ? "border-secondary bg-secondary/10 shadow-lg shadow-secondary/10"
                          : "border-zinc-700/40 hover:border-zinc-500 bg-black/20"
                        }`}
                    >
                      <div className="overflow-hidden rounded-md pointer-events-none pdf-secure-canvas">
                        <Document file={pdfSource} loading="" options={documentOptions}>
                          <Page
                            pageNumber={pNum}
                            width={140}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Document>
                      </div>
                      <span
                        className={`text-xs font-bold ${isActive ? "text-secondary" : themeClasses.textMuted
                          }`}
                      >
                        Page {pNum}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-xs text-zinc-500">
                  Thumbnails will appear when the book loads.
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* =========================================================================
          BOTTOM FLOATING DOCK (TOOLBAR)
          ========================================================================= */}
      {numPages && !loadError && (
        <div className="fixed bottom-5 inset-x-0 z-30 flex justify-center px-4 pointer-events-none">
          <nav
            className={`pointer-events-auto flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 rounded-2xl border backdrop-blur-xl transition-all ${themeClasses.dock}`}
          >
            {/* Prev Page Button */}
            <button
              onClick={prevPage}
              disabled={pageNumber <= 1}
              className={`p-2 rounded-xl transition-all ${pageNumber <= 1 ? "opacity-30 cursor-not-allowed" : themeClasses.dockBtn
                }`}
              title="Previous Page"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            {/* Page Jump Form */}
            <form onSubmit={handlePageInputSubmit} className="flex items-center gap-1.5 px-2">
              <input
                type="number"
                min={1}
                max={numPages || 1}
                value={inputPage}
                onChange={(e) => setInputPage(e.target.value)}
                onBlur={handlePageInputSubmit}
                className="w-12 sm:w-14 text-center py-1 text-xs sm:text-sm font-bold rounded-lg bg-black/20 border border-zinc-600/50 focus:border-secondary focus:outline-none"
              />
              <span className={`text-xs sm:text-sm font-semibold ${themeClasses.textMuted}`}>
                / {numPages}
              </span>
            </form>

            {/* Next Page Button */}
            <button
              onClick={nextPage}
              disabled={pageNumber >= numPages}
              className={`p-2 rounded-xl transition-all ${pageNumber >= numPages ? "opacity-30 cursor-not-allowed" : themeClasses.dockBtn
                }`}
              title="Next Page"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>

            <div className="h-5 w-px bg-zinc-700/50 mx-1 hidden sm:block" />

            {/* Zoom Out */}
            <button
              onClick={zoomOut}
              disabled={scale <= 0.6}
              className={`p-2 rounded-xl transition-all ${scale <= 0.6 ? "opacity-30 cursor-not-allowed" : themeClasses.dockBtn
                }`}
              title="Zoom Out (-)"
            >
              <MagnifyingGlassMinusIcon className="w-5 h-5" />
            </button>

            {/* Current Zoom percentage */}
            <button
              onClick={resetZoom}
              className={`px-2 py-1 text-xs font-bold tracking-tight rounded-lg ${themeClasses.dockBtn}`}
              title="Reset Zoom to 100%"
            >
              {Math.round(scale * 100)}%
            </button>

            {/* Zoom In */}
            <button
              onClick={zoomIn}
              disabled={scale >= 2.5}
              className={`p-2 rounded-xl transition-all ${scale >= 2.5 ? "opacity-30 cursor-not-allowed" : themeClasses.dockBtn
                }`}
              title="Zoom In (+)"
            >
              <MagnifyingGlassPlusIcon className="w-5 h-5" />
            </button>

            <div className="h-5 w-px bg-zinc-700/50 mx-1 hidden sm:block" />

            {/* Rotate Button */}
            <button
              onClick={rotateClockwise}
              className={`p-2 rounded-xl transition-all ${themeClasses.dockBtn}`}
              title="Rotate Page"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
